import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { useAuth } from '../../AuthContext.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { PermissionsEditor, summarizePermissions } from '../../components/PermissionsEditor.jsx';
import { ConfirmDialog } from '../../components/Modal.jsx';

const ADMIN_ROLES = new Set(['admin', 'superadmin']);

export default function Users() {
  const { user: me, role: myRole } = useAuth();
  const iAmSuper = myRole === 'superadmin';
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permsFor, setPermsFor] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [defaultsConfigured, setDefaultsConfigured] = useState(false);
  const [editDefaults, setEditDefaults] = useState(false);
  const [toast, setToast] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [userList, d] = await Promise.all([
        Admin.users.list(),
        Admin.users.getDefaultPermissions().catch(() => null),
      ]);
      setUsers(userList.users);
      if (d) {
        setDefaults(d.permissions);
        setDefaultsConfigured(d.configured);
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function toggle(u) {
    if (u.uid === me.uid) return;
    if (u.role === 'superadmin' && !iAmSuper) return;
    if (ADMIN_ROLES.has(u.role)) {
      await Admin.users.revokeAdmin(u.uid);
      setToast(`${u.email} is no longer an admin.`);
    } else {
      await Admin.users.grantAdmin(u.uid);
      setToast(
        defaultsConfigured
          ? `${u.email} promoted with configured default permissions.`
          : `${u.email} promoted with full access (no defaults configured yet).`,
      );
    }
    setTimeout(() => setToast(null), 4000);
    await refresh();
  }

  const [confirmSuper, setConfirmSuper] = useState(null);

  async function promoteSuper() {
    if (!confirmSuper) return;
    try {
      await Admin.users.grantSuperadmin(confirmSuper.uid);
      setToast(`${confirmSuper.email} is now a superadmin.`);
    } catch (e) {
      setToast(e.data?.error === 'forbidden'
        ? 'Only superadmins can grant superadmin.'
        : (e.message || 'Could not promote.'));
    } finally {
      setConfirmSuper(null);
      setTimeout(() => setToast(null), 4000);
      await refresh();
    }
  }

  async function deleteUser() {
    if (!confirmDelete) return;
    try {
      await Admin.users.remove(confirmDelete.uid);
      setToast(`${confirmDelete.email} deleted.`);
    } catch (e) {
      setToast(e.data?.error === 'cannot_modify_superadmin'
        ? 'Superadmin accounts can only be deleted by another superadmin.'
        : (e.message || 'Could not delete user.'));
    } finally {
      setConfirmDelete(null);
      setTimeout(() => setToast(null), 4000);
      await refresh();
    }
  }

  async function savePermsFor(payload) {
    await Admin.users.setPermissions(permsFor.uid, payload);
    setPermsFor(null);
    await refresh();
  }

  async function saveDefaults(payload) {
    await Admin.users.setDefaultPermissions(payload);
    setEditDefaults(false);
    await refresh();
    setToast('Default permissions saved.');
    setTimeout(() => setToast(null), 3000);
  }

  return (
    <div>
      <PageHeader
        eyebrow="People"
        title="Users"
        description="Grant admin to trusted committee members, then set what they can see and change."
      />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ink text-canvas px-4 py-2.5 rounded-full text-sm shadow-modal z-50">
          {toast}
        </div>
      )}

      {/* Defaults card */}
      <div className="card mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow mb-1">Defaults</div>
          <div className="font-display text-lg">Permissions for newly-promoted admins</div>
          <p className="text-sm text-ink-2 mt-1">
            Applied automatically when you click <em>Make admin</em>.{' '}
            {defaultsConfigured
              ? <>Current default: <strong>{summarizePermissions(defaults)}</strong>.</>
              : 'Not yet configured — new admins get full access until you set this.'}
          </p>
        </div>
        <button onClick={() => setEditDefaults(true)} className="btn-outline btn-sm">
          {defaultsConfigured ? 'Edit defaults' : 'Configure'}
        </button>
      </div>

      <div className="card-flush">
        <table className="hidden md:table w-full">
          <thead>
            <tr className="border-b border-line">
              <th className="t-head text-left px-5 py-3">Email</th>
              <th className="t-head text-left px-5 py-3">Name</th>
              <th className="t-head text-left px-5 py-3">Role</th>
              <th className="t-head text-left px-5 py-3">Access</th>
              <th className="t-head text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isAdminish = ADMIN_ROLES.has(u.role);
              const isSuper = u.role === 'superadmin';
              const isSelf = u.uid === me.uid;
              const targetLocked = isSuper && !iAmSuper; // can't touch a superadmin unless you are one
              return (
                <tr key={u.uid} className="border-b border-line last:border-0 hover:bg-line-soft/30">
                  <td className="px-5 py-3 text-sm">{u.email}</td>
                  <td className="px-5 py-3 text-sm text-ink-2">{u.displayName || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={isSuper ? 'chip-brand' : isAdminish ? 'chip-brand' : 'chip-neutral'}>
                      {isSuper ? '★ superadmin' : u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-2">
                    {isSuper ? 'Full access (locked)' : isAdminish ? summarizePermissions(u.permissions) : '—'}
                  </td>
                  <td className="px-5 py-3 text-right space-x-1">
                    {isAdminish && !isSelf && !targetLocked && !isSuper && (
                      <button className="btn-outline btn-xs" onClick={() => setPermsFor(u)}>
                        Permissions
                      </button>
                    )}
                    <button
                      onClick={() => toggle(u)}
                      disabled={isSelf || targetLocked}
                      className={isAdminish ? 'btn-ghost btn-xs text-danger' : 'btn-secondary btn-xs'}
                    >
                      {isSelf ? 'You' : isSuper ? 'Locked' : isAdminish ? 'Revoke' : 'Make admin'}
                    </button>
                    {iAmSuper && !isSelf && !isSuper && (
                      <button
                        onClick={() => setConfirmSuper(u)}
                        className="btn-ghost btn-xs text-brand"
                        title="Promote to superadmin (only superadmins can do this)"
                      >
                        ★ Super
                      </button>
                    )}
                    {!isSelf && !targetLocked && !isSuper && (
                      <button
                        onClick={() => setConfirmDelete(u)}
                        className="btn-ghost btn-xs text-danger"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="md:hidden divide-rule">
          {users.map((u) => {
            const isAdminish = ADMIN_ROLES.has(u.role);
            const isSuper = u.role === 'superadmin';
            const isSelf = u.uid === me.uid;
            const targetLocked = isSuper && !iAmSuper;
            return (
              <div key={u.uid} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{u.email}</div>
                    <div className="text-xs text-ink-2 mt-0.5">{u.displayName || '—'}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className={isAdminish ? 'chip-brand' : 'chip-neutral'}>
                        {isSuper ? '★ superadmin' : u.role}
                      </span>
                      {isAdminish && !isSuper && (
                        <span className="text-[11px] text-ink-3">{summarizePermissions(u.permissions)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {isAdminish && !isSelf && !targetLocked && !isSuper && (
                      <button className="btn-outline btn-xs" onClick={() => setPermsFor(u)}>Perms</button>
                    )}
                    <button
                      onClick={() => toggle(u)}
                      disabled={isSelf || targetLocked}
                      className={isAdminish ? 'btn-ghost btn-xs text-danger' : 'btn-secondary btn-xs'}
                    >
                      {isSelf ? 'You' : isSuper ? 'Locked' : isAdminish ? 'Revoke' : 'Admin'}
                    </button>
                    {iAmSuper && !isSelf && !isSuper && (
                      <button className="btn-ghost btn-xs text-brand" onClick={() => setConfirmSuper(u)}>
                        ★ Super
                      </button>
                    )}
                    {!isSelf && !targetLocked && !isSuper && (
                      <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirmDelete(u)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!users.length && !loading && <div className="p-10 text-center text-ink-3 text-sm italic font-display">No users yet.</div>}
      </div>
      <p className="text-[11px] text-ink-3 mt-3">Users must sign out and back in for role changes to take effect.</p>

      {permsFor && (
        <PermissionsEditor
          open
          initial={permsFor.permissions}
          eyebrow={permsFor.email}
          title="Permissions"
          onClose={() => setPermsFor(null)}
          onSave={savePermsFor}
        />
      )}

      {editDefaults && (
        <PermissionsEditor
          open
          initial={defaults}
          eyebrow="Defaults"
          title="Default permissions for new admins"
          onClose={() => setEditDefaults(false)}
          onSave={saveDefaults}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={deleteUser}
        title={`Delete ${confirmDelete?.email || 'this user'}?`}
        message="Removes the Firebase Auth account and the user record. This cannot be undone. Any data they created (players, events, etc.) stays intact."
        confirmLabel="Delete user"
        danger
      />

      <ConfirmDialog
        open={!!confirmSuper}
        onClose={() => setConfirmSuper(null)}
        onConfirm={promoteSuper}
        title={`Make ${confirmSuper?.email || 'this user'} a superadmin?`}
        message="Superadmins have full access to everything and are protected from permission edits or removal by regular admins. Only other superadmins can demote or delete them. Use sparingly — typically one or two per project."
        confirmLabel="Promote"
      />
    </div>
  );
}
